import cx from 'classnames';
import { omit } from 'lodash-es';
import React, { forwardRef } from 'react';
import styles from './IconButton.module.css';

interface ButtonCommonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  className?: string;
  icon: React.ReactNode;
  loading?: boolean;
  active?: boolean;
}

type TextSizes = 'S' | 'M';
type FabSizes = 'XS' | 'XL';

const isTextSize = (size: unknown): size is TextSizes => size === 'S' || size === 'M';
const isFabSize = (size: unknown): size is FabSizes => size === 'XS' || size === 'XL';

interface TextIconProps extends ButtonCommonProps {
  appearance: 'text';
  theme?: 'light' | 'dark';
  size?: TextSizes;
}

interface FabIconProps extends ButtonCommonProps {
  appearance: 'fab';
  size?: FabSizes;
}

export type ButtonProps = TextIconProps | FabIconProps;

const IconButton = forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => {
  const { size, className, appearance, icon, disabled, type = 'button', loading, active = false, ...rest } = props;

  const config = {
    safeSize: appearance === 'text' ? (isTextSize(size) ? size : 'S') : isFabSize(size) ? size : 'XS',
    safeTheme: appearance === 'text' ? (props.theme ?? 'light') : undefined,
    disabled: disabled || loading,
  };

  const { safeSize, safeTheme } = config;

  const buttonProps = {
    ref,
    type,
    'className': cx(styles.iconButton, className),
    'disabled': config.disabled,
    'data-appearance': appearance,
    'data-theme': safeTheme,
    'data-size': safeSize,
    'data-loading': loading,
    'data-active': active,
    'aria-busy': loading,
  };

  return (
    <button {...buttonProps} {...omit(rest, ['theme'])}>
      <span className={styles.icon}>{loading ? <img src="/gifs/spinner.gif" alt="Loading..." /> : icon}</span>
    </button>
  );
});

IconButton.displayName = 'IconButton';
export default IconButton;
